import { useEffect, useRef } from 'react';
import { isDebugEnabled } from '@/utils/debug';

/**
 * useWhyRerender
 * Logs which keys changed between renders for a given values object.
 * Useful to pinpoint which signals keep causing rerenders.
 */
export function useWhyRerender(name: string, values: Record<string, any>) {
  const prev = useRef<Record<string, any> | null>(null);

  useEffect(() => {
    if (!isDebugEnabled()) return; // no-op when debug is off
    if (prev.current) {
      const changed: Record<string, { prev: any; next: any }> = {};
      const keys = Array.from(new Set([...Object.keys(prev.current!), ...Object.keys(values)]));
      for (const key of keys) {
        const a = (prev.current as any)[key];
        const b = (values as any)[key];
        const isEqual = Object.is(a, b) || (typeof a === 'object' && typeof b === 'object' && JSON.stringify(a) === JSON.stringify(b));
        if (!isEqual) changed[key] = { prev: a, next: b };
      }
      if (Object.keys(changed).length > 0) {
        // Keep the log compact and visible (warn level shows by default)
        console.warn(`[WhyRerender] ${name} changed:`, changed);
      }
    }
    prev.current = values;
  });
}
